import React from 'react'
import ClientSuspense from '@components/ClientSuspense'
import LayoutComponent from '@components/Layout'
import SafeSuspense from '@components/SafeSuspense'
import {websiteInitialData_cached} from '@data/queries/website/__generated__/websiteInitialData_cached'
import CookiesBanner from '@page-components/CookiesBanner'
import {AddressesProviderWrapper} from '@providers/Addresses'
import WebsiteIdContext from '@providers/WebsiteIdContext'
import dynamic from 'next/dynamic'

import DeliveryContextProvider from './DeliveryContextProvider'
import InitialDataContext from './InitialDataContext'

const Website = dynamic(() => import('./Website'))
const OnTable = dynamic(() => import('./OnTable'))
const Clean = dynamic(() => import('./Clean'))
const NoWebsite = dynamic(() => import('@page-components/NoWebsite'))

export interface LayoutProps {
  initialData: websiteInitialData_cached
  theme?: string
  children: React.ReactNode
  showDemo?: boolean
}

const getLayout = (theme: string) => {
  const layouts = {
    website: Website,
    clean: Clean,
    onTable: OnTable
  }

  return layouts[theme]
}

export default function Layout(props: LayoutProps) {
  const {theme = 'website', initialData} = props

  const Component = getLayout(theme)
  if (!initialData) {
    return <NoWebsite />
  }

  return (
    <SafeSuspense
      fallback={
        <div className="initialLoadingContainer">
          <div className="initialLoadingItem" />
        </div>
      }>
      <InitialDataContext.Provider value={initialData}>
        <WebsiteIdContext.Provider value={initialData.website._id}>
          <AddressesProviderWrapper>
            <DeliveryContextProvider>
              <LayoutComponent>
                <Component website={initialData?.website} showDemo={props.showDemo}>
                  {props.children}
                </Component>
              </LayoutComponent>
            </DeliveryContextProvider>
          </AddressesProviderWrapper>
        </WebsiteIdContext.Provider>
      </InitialDataContext.Provider>
      <ClientSuspense fallback={null}>
        <CookiesBanner website={initialData.website} />
      </ClientSuspense>
    </SafeSuspense>
  )
}
